import { useEffect, useState } from "react";
import { ArrowRight, Check, Compass, Copy, Ellipsis, Maximize, PlusSquare, QrCode, Share, Zap } from "lucide-react";
import { config } from "../config";
import { t } from "../locale";
import { acknowledgeInstall } from "./InstallReminder";
import { getInstallContext, installLink } from "./install-context";

export { getInstallContext } from "./install-context";

export type InstallReason = "manual" | "trial";

export const isHomeScreen = () => matchMedia("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);

function CopyInstallLink({ url }: { url: string }) {
  const [state, setState] = useState<"idle" | "copied" | "manual">("idle");
  async function copyLink() {
    try { await navigator.clipboard.writeText(url); setState("copied"); }
    catch { setState("manual"); }
  }
  return <div className="install-copy">
    <button className="primary-button full-width" onClick={() => void copyLink()}>
      {state === "copied" ? <Check size={18} /> : <Copy size={18} />}
      {t(state === "copied" ? "Copied — open Safari" : "Copy link for Safari")}
    </button>
    {state === "manual" && <input ref={node => { node?.focus(); node?.select(); node?.setSelectionRange(0, url.length); }} value={url} readOnly aria-label={t("Website address")} onFocus={event => event.currentTarget.select()} />}
    <p className={state === "copied" ? "install-copy-next" : ""} role="status">{state === "copied" ? <><Compass size={20} aria-hidden="true" /><span>{t("Open Safari and paste into the address bar.")}<small>{t("The adding guide will open automatically in Safari.")}</small></span></> : state === "manual" ? t("Press and hold the selected address to copy it.") : ""}</p>
  </div>;
}

function PhoneHandoff({ url }: { url: string }) {
  const [image, setImage] = useState("");
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setFailed(false);
    // Load the QR encoder only when this desktop guide is opened.
    void import("qrcode").then(qr => qr.toDataURL(url, { width: 256, margin: 4, errorCorrectionLevel: "M" })).then(value => { if (active) setImage(value); }).catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [url, attempt]);
  return <div className="install-phone-handoff">
    <div className="install-qr">{image ? <img src={image} width={120} height={120} alt={t("Scan to open Yestv on your iPhone")} /> : failed ? <button onClick={() => setAttempt(value => value + 1)}>{t("Retry QR code")}</button> : <QrCode size={36} aria-label={t("Preparing QR code")} />}</div>
    <div><strong>{t("Scan with your iPhone")}</strong><p>{t("Open Camera, scan the code, then open the link in Safari.")}</p><small>{t("The adding guide will open automatically in Safari.")}</small></div>
  </div>;
}

function SafariSteps({ appName }: { appName: string }) {
  return <div className="install-safari-preview">
    <p className="install-section-label">{t("Three steps in Safari")}</p>
    <ol className="home-install-steps install-visual-steps">
      <li><span className="home-step-number">1</span><div><strong>{t("Tap Safari’s Share button")}</strong><p>{t("If hidden, open the page menu first.")}</p><div className="install-menu-sample" aria-label={t("Illustration")}><Ellipsis size={18} /><span>{t("Page menu")}</span><ArrowRight size={14} /><Share size={19} /><span>{t("Share")}</span></div></div></li>
      <li><span className="home-step-number">2</span><div><strong>{t("Choose Add to Home Screen")}</strong><p>{t("Scroll down the share menu to find it.")}</p><div className="install-menu-sample install-menu-option" aria-label={t("Illustration")}><PlusSquare size={20} /><span>{t("Add to Home Screen")}</span><PlusSquare size={16} /></div></div></li>
      <li><span className="home-step-number">3</span><div><strong>{t("Confirm the name, then tap Add")}</strong><div className="install-menu-sample install-icon-sample" aria-label={t("Illustration")}><img src="/icons/apple-touch-icon.png" alt="" /><span>{appName}</span><span className="install-add-label">{t("Add")}</span></div><p>{t("Keep Open as Web App on if this option is shown.")}</p></div></li>
    </ol>
    <p className="install-system-note">{t("Illustration: complete these actions in Safari's system menu.")}</p>
  </div>;
}

export default function InstallGuide({ onClose, reason = "manual" }: { onClose: () => void; reason?: InstallReason }) {
  const { ios, handoff, browser, desktop } = getInstallContext();
  const appName = config.locale === "zh-CN" && config.environment !== "production" ? "Yestv 测试版" : "Yestv";
  const url = installLink(location.origin, config.videoSiteOrigin, config.embedParentOrigins);
  return <>
    <div className="install-heading">
      <img src="/icons/apple-touch-icon.png" alt={t("Yestv app icon")} />
      <div><h2 id="portal-dialog-title" className="install-guide-title">{t("Add Yestv to your desktop")}</h2><p>{appName} · {t("One tap next time")}</p></div>
    </div>
    <div className="install-benefits"><span><Zap size={14} />{t("One tap to watch")}</span><span><Maximize size={14} />{t("Your own viewing window")}</span></div>
    {handoff ? <div className="install-handoff-simple">
      {desktop ? <PhoneHandoff url={url} /> : <div className="install-browser-notice"><Compass size={24} aria-hidden="true" /><div><strong>{browser ? t("Opened inside {0}", t(browser)) : t("First, open in Safari")}</strong><p>{t(ios ? "Use Safari to add this page." : "Open this link in Safari on your iPhone or iPad.")}</p></div></div>}
      <CopyInstallLink url={url} />
      <details className="install-steps-disclosure"><summary>{t("How to add it in Safari")}<span aria-hidden="true">⌄</span></summary><SafariSteps appName={appName} /></details>
      {reason === "trial" && <button className="install-quiet" onClick={onClose}>{t("Continue to registration / login")}</button>}
    </div> : <>
      <SafariSteps appName={appName} />
      <div className="install-guide-actions">
        <button className="primary-button full-width" onClick={onClose}>{t(reason === "trial" ? "Continue to registration / login" : "Got it")}</button>
      </div>
    </>}
    <div className="install-guide-options">
      {!handoff && reason === "manual" && <button className="install-quiet" onClick={() => { acknowledgeInstall(); onClose(); }}>{t("I’ve already added it")}</button>}
      <details className="install-help">
      <summary>{t("Can’t find the option?")}</summary>
      <p>{t("Scroll down the share menu. If needed, use Edit Actions to add Add to Home Screen.")}</p>
      <p>{t("If it is still missing, copy this link and open it in the Safari app.")}</p>
      {!handoff && <CopyInstallLink url={url} />}
      </details>
    </div>
  </>;
}
